import {getLangCode, interpolate} from '../stringBundle';
import utils from '../helpers/utils';
import {LL_SSDCAT} from '../constants/supportUrl';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import dateUtils from './dateUtils';
import {currencyCodes} from '../constants/currencyCodeMap';

type GetRefreshErrorMessageArgs = {
  auth: AuthState;
  institutionStatusKey: string;
  institutionName: string;
  localizedStringBundle: StringMap;
  consentExpirationDate: string;
};

interface Transaction {
  postedDate: string;
  amount?: string | number;
  currency?: string;
}
/**
 * For the given institutionState value, produce an error message with the given institutionName
 * values inserted. Returns an empty string if the institution status is "ACTIVE" (no error).
 *
 * @param {Object} params - The parameters object
 * @param {AuthState} params.auth - Auth object that contains user information
 * @param {string} params.institutionStatusKey - Error status key of institution
 * @param {string} params.institutionName - Name of institution
 * @param {StringMap} params.localizedStringBundle - Object containing localized messages
 * @param {string} params.consentExpirationDate - Expiration date of consent
 * @returns {string} The formatted error message or empty string if status is ACTIVE
 */
export const getRefreshErrorMessage = ({
  auth,
  institutionStatusKey,
  institutionName,
  localizedStringBundle,
  consentExpirationDate,
}: GetRefreshErrorMessageArgs): string => {
  const {displayLang, countryCode, partnerId, puId} = utils.getSupportUrlParams(auth);
  const supportUrl = utils.getSupportUrl('TXM_Support', displayLang, countryCode, partnerId, puId, LL_SSDCAT);
  let errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_UNKNOWN_ERROR;
  switch (institutionStatusKey) {
    case 'ACTIVE':
      return '';
      break;
    case 'PENDING':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_PENDING;
      break;
    case 'FW_USER_ACTION_REQUIRED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_USER_ACTION;
      break;
    case 'FW_ACCOUNT_DATA_NOT_AVAILABLE':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_ACCT_DATA_NOT_AVAILABLE;
      break;
    case 'FW_USER_AUTH_REQUIRED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_AUTH_ERROR;
      break;
    case 'FW_AUTH_ERROR':
    case 'FW_INTERNAL_ERROR':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_INTERNAL_ERROR;
      break;
    case 'FW_TXN_NOT_AVAILABLE':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_NOT_AVAILABLE;
      break;
    case 'FW_NOT_SUPPORTED':
    case 'FW_DISABLED_SITE_NOT_SUPPORTED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_NOT_SUPPORTED;
      break;
    case 'FW_RECONNECT_INTERNAL_ERROR':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_TECH_RECONNECT_ERROR;
      break;
    //Error code added for Yodlee specific statuses
    case 'FW_LOGIN_IN_PROGRESS':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_LOGIN_IN_PROGRESS;
      break;
    case 'FW_RETRIEVAL_IN_PROGRESS':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_RETRIEVAL_IN_PROGRESS;
      break;
    case 'FW_CONSENT_ERROR':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_CONSENT_ERROR;
      break;
    case 'FW_SITE_ERROR':
    case 'FW_TIMEOUT_ERROR':
    case 'FW_DISABLED_TEMP_ERROR':
    case 'FW_DISABLED_SITE_BLOCKING':
    case 'FW_DISABLED_UNSUBSCRIBED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_TECH_RECONNECT_ERROR;
      break;
    case 'FW_SITE_SESSION_INVALIDATED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_SESSION_INVALIDATED;
      break;
    case 'FW_DISABLED_REAL_TIME_MFA_REQUIRED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_DISABLED_REAL_TIME_MFA_REQUIRED;
      break;
    case 'FW_DISABLED_USER_ACTION_REQUIRED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_DISABLED_USER_ACTION_REQUIRED;
      break;
    case 'FW_CONSENT_RENEWAL_REQUIRED':
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_CONSENT_RENEWAL_REQUIRED;
      break;
    case 'FW_UNKNOWN':
    default:
      errorText = localizedStringBundle.FINANCIAL_WELLNESS_TXM_UNKNOWN_ERROR;
      break;
  }
  return interpolate(errorText, {
    institutionName,
    supportUrl,
    consentExpirationDate,
  });
};

/**
 * Returns cta button text for a financial institution
 * @param {string} institutionCta - cta text to display
 * @param {stringMap} localizedStringBundle - An object map with localized (translated) strings
 * @returns {string}
 */
export const getCtaText = (institutionCta: string, localizedStringBundle: StringMap): string => {
  let ctaText = localizedStringBundle.RECONNECT;
  switch (institutionCta) {
    case 'remove': {
      ctaText = localizedStringBundle.REMOVE;
      break;
    }
    case 'reconnect': {
      ctaText = localizedStringBundle.RECONNECT;
      break;
    }
    case 'update': {
      ctaText = localizedStringBundle.UPDATE;
      break;
    }
    default: {
      break;
    }
  }
  return ctaText;
};

type ConnectionReconnectFields = Pick<Connection, 'institutionStatusKey'>;

/**
 * Whether a connections API row should open the reconnect flow from a disconnect email deeplink.
 */
export function connectionRequiresReconnect(connection: ConnectionReconnectFields): boolean {
  const institutionStatusKey = String(connection.institutionStatusKey || '');
  return institutionStatusKey !== '' && institutionStatusKey !== 'ACTIVE' && institutionStatusKey !== 'PENDING';
}

export function getInstitutionErrorBanners(
  connections: Connection[],
  localizedStringBundle: StringMap,
  handleClickReconnect: Function,
  handleClickRemove: Function,
  whiteLabelClient: string,
  auth: AuthState,
  isReimagineThemeEligible: boolean
): BannerItem[] {
  if (!Array.isArray(connections) || _isEmpty(connections)) return [];

  const banners: BannerItem[] = [];

  for (const connection of connections) {
    const institutionName = _get(connection, 'institutionName', '');
    const institutionCta = _get(connection, 'institutionCtaAction', '');
    const institutionStatusKey = _get(connection, 'institutionStatusKey', '');
    const connectionId = _get(connection, 'connectionId', '') || '';
    const institutionId = _get(connection, 'institutionId', '') || '';
    const rawConsentExpirationDate = utils.safeGet(connection, 'consent.expirationDate', '');
    const consentExpirationDate = rawConsentExpirationDate
      ? dateUtils.convertDateToSlashFormat(rawConsentExpirationDate, getLangCode(), '-')
      : '';
    const errorMsg = getRefreshErrorMessage({
      auth,
      institutionStatusKey,
      institutionName,
      localizedStringBundle,
      consentExpirationDate,
    });
    const message = errorMsg;
    if (!message) continue;

    const action =
      errorMsg && institutionStatusKey != 'PENDING' ? getCtaText(institutionCta, localizedStringBundle) : '';

    const showReconnectModal = [localizedStringBundle.RECONNECT, localizedStringBundle.UPDATE].includes(action);
    const institutionDetails = {
      institutionId,
      institutionCta,
      institutionStatusKey,
    };
    const handleClick = (e: React.MouseEvent<HTMLElement>) =>
      showReconnectModal
        ? handleClickReconnect(e, connectionId, institutionDetails)
        : handleClickRemove(e, connectionId);

    banners.push({
      icon: utils.getAttentionTriangleIconUrl(isReimagineThemeEligible, whiteLabelClient),
      message,
      ...(action && {
        button: {
          label: action,
          click: handleClick,
        },
      }),
    });
  }

  return banners;
}
/**
 * Returns a formatted string representing the state of monitored accounts based on the total and connected institutions.
 *
 * - If `totalInstitutionsCount` is provided:
 *   - If both `connectedInstitutionsCount` and `totalInstitutionsCount` exceed 99, displays as `${maxCountToShow}+`.
 *   - If all institutions are connected, returns the connected count string.
 *   - Otherwise, returns a string in the format `${connectedInstitutionsCountString}/${institutionsListString}`.
 * - If `totalInstitutionsCount` is not provided:
 *   - Returns '1' if `connectedInstitutionsCount` is 1.
 *   - Returns '0' otherwise.
 *
 * @param totalInstitutionsCount - The total number of institutions to monitor.
 * @param connectedInstitutionsCount - The number of institutions currently connected.
 * @param maxCountToShow - The maximum count to display before using a "+" suffix (e.g., "99+").
 * @returns A string representing the unified state of monitored accounts.
 */
export const getMonitoredAccountUnifiedState = (
  totalInstitutionsCount: number,
  connectedInstitutionsCount: number,
  maxCountToShow: number
) => {
  if (totalInstitutionsCount) {
    const connectedInstitutionsCountString =
      connectedInstitutionsCount > 99 ? `${maxCountToShow}+` : connectedInstitutionsCount.toString();
    const institutionsListString =
      totalInstitutionsCount > 99 ? `${maxCountToShow}+` : totalInstitutionsCount.toString();

    if (connectedInstitutionsCount === totalInstitutionsCount) {
      return connectedInstitutionsCountString;
    }
    return `${connectedInstitutionsCountString}/${institutionsListString}`;
  }

  if (connectedInstitutionsCount === 1) {
    return '1';
  }

  return '0';
};

/**
 * Returns the most recent `lastRefreshDate` from an array of connection data objects,
 * formatted using `dateUtils.convertDateToSlashFormat`.
 *
 * @param connectionsData - An array of objects each containing a `lastRefreshDate` string.
 * @returns The most recent `lastRefreshDate` in slash format, or an empty string if input is empty.
 *
 * @example
 * const data = [{ lastRefreshDate: '2024-06-01T12:00:00Z' }, { lastRefreshDate: '2024-06-05T09:00:00Z' }];
 * const result = getLastUpdatedAccountDate(data);
 * // result: '06/05/2024' (format depends on implementation of convertDateToSlashFormat)
 */
export const getLastUpdatedAccountDate = (connectionsData: {lastRefreshDate: string}[]) => {
  let lastInstitutionUpdatedDate = '';
  if (Array.isArray(connectionsData) && connectionsData.length > 0) {
    lastInstitutionUpdatedDate = connectionsData.sort(
      (a, b) => new Date(b?.lastRefreshDate).getTime() - new Date(a?.lastRefreshDate).getTime()
    )[0].lastRefreshDate;
  }
  return dateUtils.convertDateToSlashFormat(lastInstitutionUpdatedDate, getLangCode(), '');
};

/**
 * Calculates the total amount of transactions from a given start date and returns it as a localized string.
 *
 * @param startDate - The ISO string representing the start date from which transactions should be considered.
 * @param data - An array of Transaction objects to process.
 * @param langCode - (Optional) The locale code used for formatting the amount string. Defaults to 'en-US'.
 * @param membershipCountryCode - (Optional) The country code used to determine the default currency. Defaults to 'US'.
 * @returns The total amount of transactions as a localized string, or undefined if there are no transactions.
 */
export const numberOfTransactions = (
  startDate: string,
  data: Transaction[],
  langCode = 'en-US',
  membershipCountryCode = 'US'
): string => {
  const transactions = data;
  let total = 0;
  let currency = currencyCodes[membershipCountryCode.toUpperCase()] || 'USD';
  if (Array.isArray(transactions) && transactions.length) {
    transactions.forEach((arrayItem: Transaction) => {
      currency = arrayItem.currency || currencyCodes[membershipCountryCode.toUpperCase()] || 'USD';
      let transactionAmount = parseFloat(arrayItem.amount as string);
      if (arrayItem.postedDate >= startDate) {
        if (arrayItem.amount) {
          transactionAmount = transactionAmount > 0 ? transactionAmount : Math.abs(transactionAmount);
          total += transactionAmount;
        }
      }
    });

    return utils.convertAmountToString(total, langCode, currency);
  }
  return utils.convertAmountToString(0, langCode, currency);
};

/**
 * Counts the number of active institutions from a list of connections.
 *
 * @param {Connection[]} institutionsList - Array of institution connections to evaluate
 * @returns {number} The count of institutions with an 'ACTIVE' status
 */
export const getConnectedInstitutionsCountUnified = (institutionsList: Connection[]) => {
  let connectedInstitutionsCount = 0;
  if (Array.isArray(institutionsList) && institutionsList.length > 0) {
    institutionsList.forEach((institution) => {
      if (institution.institutionStatusKey === 'ACTIVE') {
        connectedInstitutionsCount++;
      }
    });
  }
  return {connectedInstitutionsCount};
};
